/**
 * ProfitLossPrint — compte de résultat, vue STANDARDISÉE (≠ écran) :
 * - colonnes mois + Cumulé N / N-1 / Écart, SANS les % (noPct) ;
 * - détail ouvert pour le Chiffre d'affaires (revenue) ET le coût des ventes
 *   (cogs) ; les autres blocs restent à leur sous-total → tient sur une page.
 * Réutilise la TABLE RÉELLE de l'écran (PlTable), mode actualsOnly, lecture seule.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlView } from '../../api/financials_pl';
import { PlTable } from '../ProfitLossPage';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { p } from '../../i18n/useP';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';
import './financialsScreenPrint.css';

const noop = () => {};
const EMPTY = new Set();

/* Borne une vue au RÉEL : les mois au-delà de la clôture sont retirés des
   lignes (la vue serveur les remplit avec la prévision ; ce rapport imprime
   le réel seul, gabarit 12 mois avec « – » sur les mois futurs). */
export function clipViewToClosed(v) {
  if (!v) return v;
  const cc = Number.isFinite(v.closed_count) ? v.closed_count : 12;
  if (cc >= 12) return v;
  const strip = (r) => (r && r.months
    ? { ...r, months: Object.fromEntries(Object.entries(r.months).filter(([k]) => Number(k) <= cc)) }
    : r);
  return {
    ...v,
    rows: (v.rows || []).map(strip),
    unmapped: (v.unmapped || []).map(strip),
  };
}
// Blocs ouverts en standard : Produits (CA net) + Charges de matières (coût des ventes).
const OPEN_SECS = new Set(['revenue', 'cogs']);

export default function ProfitLossPrint({ activeTenant, year, editedAt, onDataReady, cutoff = null }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [view, setView] = useState(null);
  const [prevView, setPrevView] = useState(null);
  const [unit] = usePersistentValue(`fin_reports_unit_${tenantId}`, 'kchf');
  const thousands = unit !== 'chf';

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      getPlView(tenantId, year, cutoff ? { through: cutoff } : {}),
      getPlView(tenantId, year - 1).catch(() => ({})),
    ]).then(([cur, prev]) => {
      if (!alive) return;
      setView(clipViewToClosed(cur.data || null));
      setPrevView(prev.data || null);
    }).catch(() => {}).finally(() => { if (onDataReady) onDataReady('financials-pl'); });
    return () => { alive = false; };
  }, [tenantId, year, cutoff]); // eslint-disable-line

  if (!view) return <div className="ppl-print__section"><p>Chargement…</p></div>;

  return (
    <section className="ppl-print__section ppl-print__section--wide fin-screenprint">
      <PrintReportHeader category={p('Rapport financier')} title={t('phrases:p_a292527bbe')} year={year} unit={unit} tenant={activeTenant} editedAt={editedAt} />
      <PlTable
        view={view} prevView={prevView} thousands={thousands} actualsOnly noPct monthsFull
        brushMode={false} activeLine={null} onPickLine={noop} onPaint={noop}
        expanded={EMPTY} onToggleLine={noop} expandedSecs={OPEN_SECS} onToggleSection={noop}
        readOnly onAssign={noop} editMode={false} onEditValue={noop} onEditForecast={noop}
        onFillCs={noop} onFillTax={noop} fcMode={false} canForecast={false}
        sectionFilter={null} subtotalFilter={null} onSetMethod={noop} addToast={noop}
      />
    </section>
  );
}
